import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Path, G } from 'react-native-svg';

import { formatPercent } from '../../util/format';
import { useTranslation } from '../../i18n';

/**
 * PieChart - Simple pie/donut chart component
 *
 * @param {Array} data - Array of { label, value, color }
 * @param {number} size - Chart diameter
 * @param {number} innerRadius - Inner radius for donut (0 for pie)
 * @param {boolean} showLegend - Whether to show legend
 * @param {string} title - Optional title
 */
const PieChart = ({
  data = [],
  size = 150,
  innerRadius = 0.6,
  showLegend = true,
  title,
  style,
}) => {
  const { t } = useTranslation('analytics');

  const radius = size / 2;
  const innerRadiusPixels = radius * innerRadius;

  // Calculate total and filter out zero values
  const filteredData = data.filter((d) => d.value > 0);
  const total = filteredData.reduce((sum, d) => sum + d.value, 0);

  if (total === 0) {
    return (
      <View style={[styles.container, style]}>
        {title && <Text style={styles.title}>{title}</Text>}
        <View style={[styles.emptyChart, { width: size, height: size }]}>
          <Text style={styles.emptyText}>{t('noData')}</Text>
        </View>
      </View>
    );
  }

  // Generate pie slices
  let currentAngle = -90; // Start at top
  const slices = filteredData.map((item, index) => {
    const percentage = item.value / total;
    const angle = percentage * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    return {
      ...item,
      percentage,
      startAngle,
      endAngle,
    };
  });

  // Convert polar to cartesian
  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  // Generate arc path
  const createArc = (startAngle, endAngle, outerRadius, innerRadius) => {
    const start = polarToCartesian(radius, radius, outerRadius, endAngle);
    const end = polarToCartesian(radius, radius, outerRadius, startAngle);
    const innerStart = polarToCartesian(radius, radius, innerRadius, endAngle);
    const innerEnd = polarToCartesian(radius, radius, innerRadius, startAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

    if (innerRadius === 0) {
      // Pie slice
      return [
        `M ${radius} ${radius}`,
        `L ${end.x} ${end.y}`,
        `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${start.x} ${start.y}`,
        'Z',
      ].join(' ');
    } else {
      // Donut slice
      return [
        `M ${end.x} ${end.y}`,
        `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${start.x} ${start.y}`,
        `L ${innerStart.x} ${innerStart.y}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${innerEnd.x} ${innerEnd.y}`,
        'Z',
      ].join(' ');
    }
  };

  return (
    <View style={[styles.container, style]}>
      {title && <Text style={styles.title}>{title}</Text>}

      <View style={styles.chartContainer}>
        <Svg width={size} height={size}>
          <G>
            {slices.map((slice, index) => (
              <Path
                key={index}
                d={createArc(slice.startAngle + 90, slice.endAngle + 90, radius - 2, innerRadiusPixels)}
                fill={slice.color}
              />
            ))}
          </G>
        </Svg>

        {/* Center label for donut */}
        {innerRadius > 0 && (
          <View style={[styles.centerLabel, { width: innerRadiusPixels * 1.4, height: innerRadiusPixels * 1.4 }]}>
            <Text style={styles.centerValue}>{total}</Text>
            <Text style={styles.centerText}>{t('total')}</Text>
          </View>
        )}
      </View>

      {showLegend && (
        <View style={styles.legend}>
          {slices.map((item, index) => (
            <View key={index} style={styles.legendItem}>
              <View style={[styles.legendColor, { backgroundColor: item.color }]} />
              <View style={styles.legendTextContainer}>
                <Text style={styles.legendLabel} numberOfLines={2}>
                  {item.label}
                </Text>
                <Text style={styles.legendValue}>
                  {item.value} ({formatPercent(item.percentage * 100)})
                </Text>
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  // V3: Card styling matches Submit Report cards
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#F0F0F0',
    overflow: 'hidden',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  chartContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  centerLabel: {
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
  },
  centerValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#111827',
  },
  centerText: {
    fontSize: 12,
    color: '#6b7280',
  },
  legend: {
    marginTop: 16,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 10,
    minHeight: 24,
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 3,
    marginRight: 10,
    marginTop: 3,
  },
  legendTextContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  legendLabel: {
    fontSize: 13,
    color: '#374151',
    flex: 1,
    lineHeight: 18,
    paddingRight: 8,
  },
  legendValue: {
    fontSize: 13,
    fontWeight: '500',
    color: '#6b7280',
    minWidth: 60,
    textAlign: 'right',
  },
  emptyChart: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    borderRadius: 75,
  },
  emptyText: {
    fontSize: 14,
    color: '#9ca3af',
  },
});

export default PieChart;
